import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { SEOHead } from "@/components/SEOHead";
import { Truck, CalendarIcon, Lock, Store, MapPin } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";

const TIME_SLOTS = [
  { id: "11:30-15:30", label: "11:30 AM – 3:30 PM", startMin: 11 * 60 + 30 },
  { id: "15:30-19:30", label: "3:30 PM – 7:30 PM", startMin: 15 * 60 + 30 },
  { id: "19:30-23:30", label: "7:30 PM – 11:30 PM", startMin: 19 * 60 + 30 },
];

const PAYMENT_METHODS = [
  {
    id: "razorpay",
    label: "Pay Online",
    desc: "UPI · Cards · Net Banking · Wallets — securely via Razorpay",
  },
];

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const startOfToday = () => startOfDay(new Date());
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const isSameDay = (a, b) => startOfDay(a).getTime() === startOfDay(b).getTime();

// Slots whose start time is strictly in the future (used only when the
// selected delivery date is today).
const getTodayAvailableSlots = () => {
  const now = new Date();
  const currentMin = now.getHours() * 60 + now.getMinutes();
  return TIME_SLOTS.filter((s) => s.startMin > currentMin);
};

// Hoisted to module scope — redefining this inside <Checkout /> on every render
// caused React to unmount/remount inputs on each keystroke (losing focus).
const Field = ({ label, required, children, error }) => (
  <label className="block">
    <span className="block text-sm mb-1.5" style={{ color: "var(--ink)" }}>
      {label} {required && <span style={{ color: "var(--accent)" }}>*</span>}
    </span>
    {children}
    {error && <span className="block text-xs mt-1" style={{ color: "#B85C3D" }}>{error}</span>}
  </label>
);

// Reusable date + time-slot + earliest-copy block. Hoisted to module scope
// so it doesn't get remounted on every keystroke of the parent form.
const DateTimePicker = ({
  dateLabel,
  slotLabel,
  earliestPrefix,
  date,
  setDate,
  timeSlot,
  onSlotChange,
  slots,
  nextAvailable,
  startOfToday,
}) => (
  <div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Field label={dateLabel} required>
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="input-field text-left flex items-center gap-3" data-testid="ck-date-picker">
              <CalendarIcon size={16} strokeWidth={1.5} />
              {date ? format(date, "EEE, dd MMM yyyy") : "Pick a date"}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={(d) => d && setDate(d)}
              disabled={(d) => d < startOfToday()}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </Field>
      <Field label={slotLabel} required>
        <select
          className="input-field"
          value={timeSlot}
          onChange={(e) => onSlotChange(e.target.value)}
          data-testid="ck-time-slot"
        >
          {slots.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
      </Field>
    </div>
    <p className="text-xs mt-3" style={{ color: "var(--ink-soft)" }} data-testid="ck-earliest-copy">
      {earliestPrefix}: {nextAvailable.dayLabel}, {nextAvailable.slot.label}.
    </p>
  </div>
);

export default function Checkout() {
  const { cart, subtotal, clearCart, setCartOpen, user } = useApp();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  // Same-day delivery: if any slot's start is still in the future, default
  // date = today, else roll forward to tomorrow.
  const initialTodaySlots = useMemo(() => getTodayAvailableSlots(), []);
  const initialDate = useMemo(
    () => (initialTodaySlots.length > 0 ? startOfToday() : addDays(startOfToday(), 1)),
    [initialTodaySlots],
  );
  const [date, setDate] = useState(initialDate);

  const [form, setForm] = useState({
    first_name: user?.name?.split(" ")[0] || "",
    last_name: user?.name?.split(" ").slice(1).join(" ") || "",
    customer_email: user?.email || "",
    customer_phone: "",
    fulfillment_method: "delivery", // "delivery" | "pickup"
    delivery_address: "",
    apartment: "",
    city: "Mumbai",
    state: "Maharashtra",
    pin_code: "",
    delivery_time_slot: (initialTodaySlots[0] || TIME_SLOTS[0]).id,
    payment_method: "razorpay",
    notes: "",
  });

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isPickup = form.fulfillment_method === "pickup";

  // Slots available for the currently-picked delivery date. Today filters
  // out slots whose start time has passed; any future date shows all three.
  const slotsForSelectedDate = useMemo(() => {
    if (isSameDay(date, new Date())) return getTodayAvailableSlots();
    return TIME_SLOTS;
  }, [date]);

  // If the current slot is no longer valid for the selected date, snap
  // to the earliest available one.
  useEffect(() => {
    if (!slotsForSelectedDate.find((s) => s.id === form.delivery_time_slot)) {
      setForm((f) => ({
        ...f,
        delivery_time_slot: slotsForSelectedDate[0]?.id || "",
      }));
    }
  }, [slotsForSelectedDate, form.delivery_time_slot]);

  // Overall earliest slot copy — reflects "next available" regardless of
  // whether the user has picked a different date manually.
  const nextAvailable = useMemo(() => {
    const todaySlots = getTodayAvailableSlots();
    if (todaySlots.length > 0) return { dayLabel: "Today", slot: todaySlots[0] };
    return { dayLabel: "Tomorrow", slot: TIME_SLOTS[0] };
  }, []);

  // Email validation
  const emailValid = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.customer_email),
    [form.customer_email]
  );
  const phoneValid = useMemo(
    () => /^\d{10}$/.test(form.customer_phone),
    [form.customer_phone]
  );
  const pinValid = useMemo(() => /^\d{6}$/.test(form.pin_code), [form.pin_code]);
  const requiredFilled =
    form.first_name &&
    form.last_name &&
    emailValid &&
    phoneValid &&
    (isPickup || (form.delivery_address && form.city && form.state && pinValid)) &&
    date &&
    form.delivery_time_slot;

  // Auto-populate city + state from PIN code via free India postal API
  useEffect(() => {
    if (!pinValid) return;
    let cancelled = false;
    fetch(`https://api.postalpincode.in/pincode/${form.pin_code}`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const pos = Array.isArray(data) ? data[0] : null;
        const office = pos?.PostOffice?.[0];
        if (office) {
          setForm((f) => ({
            ...f,
            city: office.District || f.city,
            state: office.State || f.state,
          }));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [form.pin_code, pinValid]);

  // Flat ₹300 delivery — waived for store pickup.
  const DELIVERY_FEE = 300;
  const deliveryFee = isPickup ? 0 : DELIVERY_FEE;
  const total = subtotal + deliveryFee;

  const submit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) {
      toast.error("Your basket is empty.");
      return;
    }
    if (!requiredFilled) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: cart.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          image: i.image,
          variant_label: i.variant_label,
          bundle_selection: i.bundle_selection || null,
        })),
        first_name: form.first_name,
        last_name: form.last_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone, // 10 digits — backend prepends +91
        fulfillment_method: form.fulfillment_method,
        delivery_address: isPickup ? "" : form.delivery_address,
        apartment: isPickup ? "" : form.apartment,
        city: isPickup ? "" : form.city,
        state: isPickup ? "" : form.state,
        pin_code: isPickup ? "" : form.pin_code,
        delivery_date: date.toISOString(),
        delivery_time_slot: form.delivery_time_slot,
        payment_method: form.payment_method,
        notes: form.notes,
      };
      const r = await api.post("/orders", payload);
      const order = r.data;

      // Create a Razorpay order server-side
      const rzp = await api.post("/payments/razorpay/create-order", {
        order_id: order.id,
      });

      // Open Razorpay checkout modal
      if (typeof window.Razorpay !== "function") {
        toast.error("Payment library failed to load. Please refresh and try again.");
        setSubmitting(false);
        return;
      }

      const options = {
        key: rzp.data.key_id,
        amount: rzp.data.amount,
        currency: rzp.data.currency,
        order_id: rzp.data.razorpay_order_id,
        name: "Cocoamaya Patisserie",
        description: `Order ${order.id.slice(0, 8)}`,
        image: "/cocoamaya-bakery.png",
        prefill: {
          name: rzp.data.customer.name,
          email: rzp.data.customer.email,
          contact: rzp.data.customer.contact,
        },
        theme: { color: "#4A5D3A" },
        notes: { internal_order_id: order.id },
        handler: async function (response) {
          try {
            await api.post("/payments/razorpay/verify", {
              order_id: order.id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            clearCart();
            window.location.href = `/order-success?order_id=${order.id}&razorpay_payment_id=${response.razorpay_payment_id}`;
          } catch (err) {
            toast.error(err.response?.data?.detail || "Payment verification failed. Please contact support.");
            setSubmitting(false);
          }
        },
        modal: {
          ondismiss: function () {
            toast("Payment cancelled. Your order is still saved — you can retry from the Track Order page.");
            setSubmitting(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", function (resp) {
        toast.error(resp.error?.description || "Payment failed. Please try again.");
        setSubmitting(false);
      });
      razorpay.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to place order");
      setSubmitting(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="empty-checkout">
        <SEOHead title="Checkout | Cocoamaya Patisserie" noindex />
        <h1 className="font-display text-4xl mb-6">Nothing in your basket yet.</h1>
        <Link to="/shop" className="btn-primary inline-flex">Visit the shop</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14" data-testid="checkout-page">
      <SEOHead title="Checkout | Cocoamaya Patisserie" noindex />
      <div className="mb-10">
        <p className="eyebrow mb-2">Step 02 of 02</p>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight italic">Checkout</h1>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-10">
          {/* Contact */}
          <section>
            <h2 className="font-display text-2xl mb-5">Contact</h2>
            <Field label="Email address" required error={form.customer_email && !emailValid ? "Enter a valid email" : null}>
              <input
                type="email"
                className="input-field"
                placeholder="you@example.com"
                value={form.customer_email}
                onChange={(e) => onChange("customer_email", e.target.value)}
                data-testid="ck-email"
                required
              />
            </Field>
          </section>

          {/* Fulfillment method: Delivery vs Store Pickup */}
          <section>
            <h2 className="font-display text-2xl mb-5">How would you like to receive your order?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start" role="radiogroup" aria-label="Fulfillment method">
              {/* --- Standard Delivery card --- */}
              <div
                className="border rounded-lg overflow-hidden transition-colors"
                style={{
                  borderColor: !isPickup ? "var(--accent)" : "var(--line)",
                  background: !isPickup ? "var(--bg-card)" : "transparent",
                }}
                data-testid="fulfillment-delivery-card"
              >
                <label
                  className="flex items-start gap-3 p-5 cursor-pointer"
                  data-testid="fulfillment-delivery"
                >
                  <input
                    type="radio"
                    name="fulfillment"
                    value="delivery"
                    checked={!isPickup}
                    onChange={() => onChange("fulfillment_method", "delivery")}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <p className="font-medium flex items-center gap-2">
                      <Truck size={16} strokeWidth={1.5} /> Standard Delivery
                    </p>
                    <p className="text-sm mt-1" style={{ color: "var(--ink-soft)" }}>
                      Flat ₹300 across Mumbai. Earliest: {nextAvailable.dayLabel}, {nextAvailable.slot.label}.
                    </p>
                  </div>
                </label>
                {!isPickup && (
                  <div className="px-5 pb-5 pt-1 border-t" style={{ borderColor: "var(--line)" }} data-testid="delivery-datetime">
                    <DateTimePicker
                      dateLabel="Delivery date"
                      slotLabel="Delivery slot"
                      earliestPrefix="Earliest delivery"
                      date={date}
                      setDate={setDate}
                      timeSlot={form.delivery_time_slot}
                      onSlotChange={(v) => onChange("delivery_time_slot", v)}
                      slots={slotsForSelectedDate}
                      nextAvailable={nextAvailable}
                      startOfToday={startOfToday}
                    />
                  </div>
                )}
              </div>

              {/* --- Store Pickup card --- */}
              <div
                className="border rounded-lg overflow-hidden transition-colors"
                style={{
                  borderColor: isPickup ? "var(--accent)" : "var(--line)",
                  background: isPickup ? "var(--bg-card)" : "transparent",
                }}
                data-testid="fulfillment-pickup-card"
              >
                <label
                  className="flex items-start gap-3 p-5 cursor-pointer"
                  data-testid="fulfillment-pickup"
                >
                  <input
                    type="radio"
                    name="fulfillment"
                    value="pickup"
                    checked={isPickup}
                    onChange={() => onChange("fulfillment_method", "pickup")}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <p className="font-medium flex items-center gap-2">
                      <Store size={16} strokeWidth={1.5} /> Store Pickup <span className="text-xs" style={{ color: "var(--accent)" }}>· Free</span>
                    </p>
                    <p className="text-sm mt-1" style={{ color: "var(--ink-soft)" }}>
                      Collect from our Andheri East store — no shipping fee.
                    </p>
                  </div>
                </label>
                {isPickup && (
                  <div className="px-5 pb-5 pt-1 border-t space-y-4" style={{ borderColor: "var(--line)" }} data-testid="pickup-datetime">
                    <div
                      className="p-4 rounded-lg border flex gap-3"
                      style={{ borderColor: "var(--accent)" }}
                      data-testid="store-location-card"
                    >
                      <MapPin size={20} strokeWidth={1.5} style={{ color: "var(--accent)", flexShrink: 0 }} />
                      <div>
                        <p className="eyebrow mb-1">Pickup location</p>
                        <p className="text-sm leading-relaxed">
                          Ground Floor, Peninsula Grand Hotel,<br />
                          90 Feet Road, Sakinaka,<br />
                          Andheri-Kurla Rd, Andheri East,<br />
                          Mumbai, Maharashtra 400072
                        </p>
                      </div>
                    </div>
                    <DateTimePicker
                      dateLabel="Pickup date"
                      slotLabel="Pickup slot"
                      earliestPrefix="Earliest pickup"
                      date={date}
                      setDate={setDate}
                      timeSlot={form.delivery_time_slot}
                      onSlotChange={(v) => onChange("delivery_time_slot", v)}
                      slots={slotsForSelectedDate}
                      nextAvailable={nextAvailable}
                      startOfToday={startOfToday}
                    />
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Delivery */}
          <section>
            <h2 className="font-display text-2xl mb-5">
              {isPickup ? "Your details" : "Delivery details"}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="First name" required>
                <input className="input-field" value={form.first_name} onChange={(e) => onChange("first_name", e.target.value)} required data-testid="ck-first-name" />
              </Field>
              <Field label="Last name" required>
                <input className="input-field" value={form.last_name} onChange={(e) => onChange("last_name", e.target.value)} required data-testid="ck-last-name" />
              </Field>
            </div>

            <div className="mt-4">
              <Field label="Mobile number" required error={form.customer_phone && !phoneValid ? "Enter a 10-digit mobile number" : null}>
                <div className="flex">
                  <span className="input-field rounded-r-none border-r-0 flex items-center justify-center px-3 flex-shrink-0" style={{ background: "var(--bg-soft)", color: "var(--ink-soft)", width: "60px" }}>
                    +91
                  </span>
                  <input
                    inputMode="numeric"
                    pattern="[0-9]{10}"
                    maxLength={10}
                    className="input-field rounded-l-none"
                    placeholder="10-digit mobile number"
                    value={form.customer_phone}
                    onChange={(e) => onChange("customer_phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
                    required
                    data-testid="ck-phone"
                  />
                </div>
              </Field>
            </div>

            {!isPickup && (
              <>
                <div className="mt-4">
                  <Field label="Address" required>
                    <input className="input-field" placeholder="Street address" value={form.delivery_address} onChange={(e) => onChange("delivery_address", e.target.value)} required data-testid="ck-address" />
                  </Field>
                </div>
                <div className="mt-4">
                  <Field label="Apartment, suite, etc. (optional)">
                    <input className="input-field" value={form.apartment} onChange={(e) => onChange("apartment", e.target.value)} data-testid="ck-apartment" />
                  </Field>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                  <Field label="PIN code" required error={form.pin_code && !pinValid ? "6-digit PIN" : null}>
                    <input
                      inputMode="numeric"
                      maxLength={6}
                      className="input-field"
                      value={form.pin_code}
                      onChange={(e) => onChange("pin_code", e.target.value.replace(/\D/g, "").slice(0, 6))}
                      required
                      data-testid="ck-pin"
                    />
                  </Field>
                  <Field label="City" required>
                    <input className="input-field" value={form.city} onChange={(e) => onChange("city", e.target.value)} required data-testid="ck-city" />
                  </Field>
                  <Field label="State" required>
                    <input className="input-field" value={form.state} onChange={(e) => onChange("state", e.target.value)} required data-testid="ck-state" />
                  </Field>
                </div>
              </>
            )}
          </section>

          {/* Shipping section — hidden when pickup */}
          {!isPickup && (
            <section>
              <h2 className="font-display text-2xl mb-5">Shipping</h2>
              <div className="border rounded-lg p-5" style={{ borderColor: "var(--line)", background: "var(--bg-card)" }}>
                <div className="flex items-start gap-3" data-testid="ship-flat">
                  <Truck size={20} strokeWidth={1.5} style={{ color: "var(--accent)" }} />
                  <div className="flex-1">
                    <div className="flex justify-between items-baseline">
                      <p className="font-medium">Standard delivery</p>
                      <p className="font-medium">₹{deliveryFee}</p>
                    </div>
                    <p className="text-sm mt-1" style={{ color: "var(--ink-soft)" }}>
                      Flat rate across Mumbai. Earliest delivery: {nextAvailable.dayLabel}, {nextAvailable.slot.label}.
                    </p>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Payment */}
          <section>
            <h2 className="font-display text-2xl mb-5">Payment</h2>
            <p className="text-xs mb-4 inline-flex items-center gap-1.5" style={{ color: "var(--ink-soft)" }}>
              <Lock size={12} strokeWidth={1.5} /> All transactions are secure and encrypted via Razorpay.
            </p>
            <div className="space-y-3">
              {PAYMENT_METHODS.map((opt) => (
                <label
                  key={opt.id}
                  className="flex items-start gap-4 p-5 border rounded-lg cursor-pointer transition-colors"
                  style={{
                    borderColor: form.payment_method === opt.id ? "var(--accent)" : "var(--line)",
                    background: form.payment_method === opt.id ? "var(--bg-card)" : "transparent",
                  }}
                  data-testid={`pay-${opt.id}`}
                >
                  <input type="radio" name="pay" checked={form.payment_method === opt.id} onChange={() => onChange("payment_method", opt.id)} className="mt-1" />
                  <div className="flex-1">
                    <p className="font-medium">{opt.label}</p>
                    <p className="text-sm" style={{ color: "var(--ink-soft)" }}>{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </section>

          <textarea
            className="input-field"
            rows={2}
            placeholder="Order notes (optional, e.g. 'leave at door')"
            value={form.notes}
            onChange={(e) => onChange("notes", e.target.value)}
            data-testid="ck-notes"
          />
        </div>

        {/* Order summary */}
        <aside className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
          <div className="p-7 rounded-xl border" style={{ borderColor: "var(--line)", background: "var(--bg-card)" }}>
            <div className="flex justify-between items-center mb-5">
              <p className="eyebrow">Order summary</p>
              <button
                type="button"
                onClick={() => setCartOpen(true)}
                className="text-xs underline"
                style={{ color: "var(--accent)" }}
                data-testid="ck-edit-cart"
              >
                Edit
              </button>
            </div>
            <ul className="space-y-4">
              {cart.map((i) => (
                <li key={i.cart_key} className="flex gap-3 items-start" data-testid={`ck-item-${i.cart_key}`}>
                  <div className="relative w-16 h-16 rounded-md overflow-hidden flex-shrink-0" style={{ background: "var(--bg-soft)" }}>
                    {i.image && <img src={i.image} alt={i.name} className="w-full h-full object-cover" />}
                    <span className="absolute -top-1 -right-1 text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" style={{ background: "var(--accent)", color: "#FAF3EB" }}>
                      {i.quantity}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium leading-tight">{i.name}</p>
                    <p className="text-xs" style={{ color: "var(--ink-soft)" }}>₹{Math.round(i.price)} each</p>
                    {i.bundle_selection && i.bundle_selection.length > 0 && (
                      <p className="text-[11px] mt-1 leading-snug" style={{ color: "var(--ink-soft)" }} data-testid={`ck-bundle-${i.cart_key}`}>
                        {(() => {
                          const c = i.bundle_selection.reduce((m, n) => ({ ...m, [n]: (m[n] || 0) + 1 }), {});
                          return Object.entries(c).map(([n, q]) => (q > 1 ? `${q}× ${n}` : n)).join(", ");
                        })()}
                      </p>
                    )}
                  </div>
                  <p className="text-sm">₹{Math.round(i.price * i.quantity)}</p>
                </li>
              ))}
            </ul>

            <div className="mt-6 pt-5 border-t space-y-2 text-sm" style={{ borderColor: "var(--line)", color: "var(--ink-soft)" }}>
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span data-testid="ck-subtotal" style={{ color: "var(--ink)" }}>₹{Math.round(subtotal)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="inline-flex items-center gap-1.5">
                  {isPickup ? <Store size={13} strokeWidth={1.5} /> : <Truck size={13} strokeWidth={1.5} />}
                  {isPickup ? "Store pickup" : "Shipping"}
                </span>
                <span data-testid="ck-delivery-fee" style={{ color: "var(--ink)" }}>
                  {isPickup ? "Free" : `₹${deliveryFee}`}
                </span>
              </div>
            </div>

            <div className="mt-5 pt-5 border-t flex justify-between items-baseline" style={{ borderColor: "var(--line)" }}>
              <span className="font-display text-lg">Total</span>
              <span className="font-display text-3xl" data-testid="ck-total" style={{ color: "var(--ink)" }}>
                ₹{Math.round(total)}
              </span>
            </div>

            <button
              type="submit"
              className="btn-primary w-full justify-center mt-6"
              disabled={submitting || !requiredFilled}
              data-testid="place-order-btn"
            >
              {submitting
                ? "Opening payment…"
                : !requiredFilled
                ? "Complete details to continue"
                : `Pay ₹${Math.round(total)} securely`}
            </button>

            <p className="text-xs mt-4 text-center" style={{ color: "var(--ink-soft)" }}>
              By placing your order you agree to our terms.
            </p>
          </div>
        </aside>
      </form>
    </div>
  );
}
